import { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { useTheme } from "styled-components";

import { UserClaims, AuthContext } from "core/contexts/auth/auth";
import { getCasesMetrics } from "modules/ac2/services/alertService";
import { getUsersService } from "core/services/ac2Flywheel/userService";
import { exportToCSV } from "core/hooks/tableToCsv";
import { getFilterCount, getFilters } from "core/hooks/filters";
import { Toast as showToast } from "core/components/global/toast/toast";

import {
  assignUserToAlert,
  fetchAllAlertsByPage,
  setAlertStatus,
  setAlertsByPage,
  setExportLoading,
  setPreviewWidth,
  setSelectedRow,
  setSideModal,
  setWeeklyAlerts,
} from "modules/ac2/store/alert";

import { IHeadCell } from "core/components/global/tables/datagrid/dataGridHeader/datagridHeader";
import { AlertsBox } from "./alerts.styled";
import Datagrid from "core/components/global/tables/datagrid/dataGrid";
import GridItem from "core/components/layout/grid/gridItem/gridItem";
import { setFiltersApplied } from "core/store/shared/header";
import CategoryAlertsToolbar from "./categoryAlertsToolbar";
import AlertSidePanel from "./alertSidePanel";

export interface ISortData {
  field: string;
  order: string;
}

export interface ShowMoreState {
  [key: string]: boolean;
}

interface Params {
  target: string;
  aFAccessToken: string;
  page: number;
  size: number;
  orderBy: string;
  order: string;
  filters: object;
  search: string;
}

export default function CategoryAlertsTable() {
  const { t } = useTranslation();
  const { checkAuthToken } = useContext(AuthContext);
  const theme = useTheme();

  const alertState = useAppSelector((state) => {
    console.log("Current alerts state:", state.alerts);
    return state.alerts;
  });

  const dispatch = useAppDispatch();
  const [users, setUsers] = useState<UserClaims[]>([]);

  // const [topGridBoxdata, setTopGridBoxdata] = useState<AlertsTopGridBox[]>([
  const [topGridBoxdata, setTopGridBoxdata] = useState<any>([
    { num: 0, type: t("TOP_GRID_BOX.TOTAL_CASES"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.CRITICAL"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.HIGH"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.MEDIUM"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.LOW"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.INFORMATIVE"), color: "fff" },
  ]);

  const statusMapping = {
    [t("STATUS.OPEN")]: theme.colors.open_incident,
    [t("STATUS.IN_PROGRESS")]: theme.colors.in_progress_incident,
    [t("STATUS.CLOSED")]: theme.colors.closed_incident,
    [t("STATUS.REQUEST_TUNING")]: theme.colors.requested_incident,
  };

  /**
   * Fetches the full list of active and inactive users on this platform
   */
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = await checkAuthToken();
        const config = {
          Authorization: token,
          "Content-Type": "application/json",
        };
        const usersData = await getUsersService(config);
        setUsers(usersData);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, []);

  const handleSetStatus = async (status: string, id: string) => {
    if (status) {
      const token = await checkAuthToken();
      dispatch(
        setAlertStatus({ aFAccessToken: token, status: status, id: id })
      );
    }
  };

  /**
   * Function to manage the assigned to field
   * @param email
   * @param id
   */
  const handleAssignedTo = async (email: string, id: string) => {
    const token = await checkAuthToken();
    if (email) {
      dispatch(
        assignUserToAlert({ aFAccessToken: token, email: email, id: id })
      );
    } else {
      dispatch(assignUserToAlert({ aFAccessToken: token, email: "", id: id }));
    }
  };

  async function getGridBoxData() {
    const token = await checkAuthToken();
    const config = { Authorization: token, "Content-Type": "application/json" };
    const gridBoxData = await getCasesMetrics(config);
    setTopGridBoxdata(gridBoxData);
  }

  /**
   * Update the gridData
   */
  useEffect(() => {
    getGridBoxData();
  }, [alertState.pageEvents]);

  /**
   * Set the states and function to handle the sorting and filtering functionalities
   * within the common table component
   */
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterData, setFilterData] = useState({
    totalFilters: 0,
    filterTerms: {},
    searchTerm: "",
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [sortData, setSortData] = useState<ISortData>({
    field: "passfail",
    order: "asc",
  });

  type Section = { title: string; [key: string]: any };
  const [descriptions, setDescriptions] = useState<Section | null>(null);
  const [framework, setFramework] = useState("");
  const [selectedDescription, setSelectedDescription] = useState("");
  const [rawRuleName, setRawRuleName] = useState("");
  const [selectedSectionTitle, setSelectedSectionTitle] = useState("");

  // Get framework data from Redux store instead of localStorage
  useEffect(() => {
    const frameworkData = alertState.frameworkData;
    console.log("Framework data from Redux:", frameworkData);

    if (frameworkData.descriptions && frameworkData.descriptions.length > 0) {
      setDescriptions({
        title: frameworkData.selectedSectionTitle || "",
        findings: frameworkData.descriptions,
      });
    }
    if (frameworkData.selectedFramework) {
      setFramework(frameworkData.selectedFramework);
    }
    if (frameworkData.selectedDescription) {
      setSelectedDescription(frameworkData.selectedDescription);
    }
    if (frameworkData.selectedRawRuleName) {
      // Store the raw rule name in state for filtering
      setRawRuleName(frameworkData.selectedRawRuleName);
    }
    if (frameworkData.selectedSectionTitle) {
      setSelectedSectionTitle(frameworkData.selectedSectionTitle);
    }
    console.log("Framework:", frameworkData.selectedFramework);
    console.log("Descriptions:", frameworkData.descriptions);
    console.log("Selected Description:", frameworkData.selectedDescription);
    console.log("Selected Section Title:", frameworkData.selectedSectionTitle);
  }, [alertState.frameworkData]);

  useEffect(() => {
    if (framework && rawRuleName) {
      console.log("Applying filter with framework:", framework);
      console.log("Applying filter with rule:", rawRuleName);
      handleFilterData({
        filter: {
          framework: framework,
          rule: rawRuleName,
        },
      });
    }
  }, [framework, rawRuleName]);

  // Check if we're on the 'alerts cases' route which shouldn't have base filters
  useEffect(() => {
    const path = window.location.pathname;
    // If we're on the cases route, we don't want to apply base filters
    if (path.includes("/ac2/cases")) {
      // Don't apply any base filters
      console.log("On cases route - not applying base filters");
      // Clear existing filters if any
      setFramework("");
      setRawRuleName("");
      setSelectedDescription("");
      setSelectedSectionTitle("");
    }
  }, []);

  async function onSortChange(field: string, order: string) {
    setSortData({ field, order });
    handleFilterData({
      filter: {},
      argSortOrder: order,
      argSortField: field === "id" ? "alertId" : field,
    });
  }

  async function onPageChange(pageNum) {
    handleFilterData({ filter: {}, argPageNum: pageNum });
  }

  async function handleRowsPerPageChange(pageSize) {
    setPageSize(pageSize);
    handleFilterData({ filter: {}, argPageSize: pageSize });
  }

  async function clearSearch() {
    const token = await checkAuthToken();

    setFilterData((prev) => ({
      filterTerms: filterData["filterTerms"],
      totalFilters: getFilterCount("", filterData["filterTerms"]),
      searchTerm: "",
    }));

    dispatch(
      setAlertsByPage({
        target: "cases",
        aFAccessToken: token,
        page: page,
        size: pageSize,
        orderBy: sortData.field,
        order: sortData.order,
        filters: filterData["filterTerms"],
        search: "",
      })
    );
  }

  async function deleteFilter(key, value) {
    const token = await checkAuthToken();

    const tempTerms: { [key: string]: any } = filterData["filterTerms"];
    const filterValues = tempTerms[key];

    if (filterValues) {
      tempTerms[key] = filterValues
        .split(",")
        .filter((item) => item !== value)
        .join(",");

      for (const key in tempTerms) {
        if (tempTerms[key] === "") {
          delete tempTerms[key];
        }
      }

      setFilterData((prev) => ({
        filterTerms: tempTerms,
        totalFilters: getFilterCount(
          filterData["searchTerm"],
          filterData["filterTerms"]
        ),
        searchTerm: filterData["searchTerm"],
      }));

      dispatch(
        setAlertsByPage({
          target: "cases",
          aFAccessToken: token,
          page: page,
          size: pageSize,
          orderBy: sortData.field,
          order: sortData.order,
          filters: tempTerms ? tempTerms : filterData["filterTerms"],
          search: filterData["searchTerm"],
        })
      );
    }
  }

  async function clearFilters() {
    const token = await checkAuthToken();

    setFilterData((prev) => ({
      filterTerms: {},
      totalFilters: 0,
      searchTerm: "",
    }));

    dispatch(
      setAlertsByPage({
        target: "home",
        aFAccessToken: token,
        page: page,
        size: pageSize,
        orderBy: sortData.field,
        order: sortData.order,
        filters: {},
        search: "",
      })
    );
  }

  // Clear Filter button for the Mobile Desktop
  async function handleSearchValue({ search }) {
    // Verify JWT from auth server
    const token = await checkAuthToken();

    // Build Params for the HTTP request
    const params: Params = {
      target: "cases",
      aFAccessToken: token,
      page: page,
      size: pageSize,
      orderBy: sortData.field,
      order: sortData.order,
      filters: filterData["filterTerms"],
      search: search,
    };

    // Send the HTTP request
    dispatch(setAlertsByPage(params));

    setFilterData({
      totalFilters: getFilterCount(search, filterData["filterTerms"]),
      filterTerms: filterData["filterTerms"],
      searchTerm: search,
    });

    // For Mobile/Tablet update the header widget state
    dispatch(setFiltersApplied(true));
  }

  async function handleFilterData({
    filter,
    argPageSize = pageSize,
    argPageNum = page,
    argSortField = sortData.field,
    argSortOrder = sortData.order,
  }) {
    const token = await checkAuthToken();

    const params: Params = {
      target: "cases",
      aFAccessToken: token,
      page: argPageNum,
      size: argPageSize,
      orderBy: argSortField,
      order: argSortOrder,
      filters: filterData["filterTerms"],
      search: filterData["searchTerm"],
    };

    // A new filter has been provided, parse an add replace the existing
    params.filters = getFilters(filter, params);
    console.log("Final params being sent to API:", params);

    // Request the filtered data from the backend
    const result = await dispatch(setAlertsByPage(params));
    console.log("Result from setAlertsByPage:", result);

    setFilterData({
      totalFilters: getFilterCount(filterData["searchTerm"], params.filters),
      filterTerms: params.filters,
      searchTerm: filterData["searchTerm"],
    });

    // For Mobile/Tablet update the header widget state
  }

  /**
   * Export the filtered data to Excel
   */
  async function handleExportButton() {
    dispatch(setExportLoading(true));
    const token = await checkAuthToken();

    const params: Params = {
      target: "cases",
      aFAccessToken: token,
      page: page,
      size: pageSize,
      orderBy: sortData.field,
      order: sortData.order,
      filters: filterData["filterTerms"],
      search: filterData["searchTerm"],
    };

    const pageEvents = await dispatch(fetchAllAlertsByPage(params));
    const payload: any = pageEvents["payload"];

    await exportToCSV(payload["exportEvents"]["records"]);

    dispatch(setExportLoading(false));
  }

  /**
   * Handle the expanded alert modal
   * @param id
   */

  const onAlertClick = async (id) => {    
      console.log('onAlertClick called with ID:', id);
      if (id) {
        try {
          // Set the selected row ID and open the preview pane
          dispatch(setSelectedRow(id));
          console.log('Dispatching setSelectedRow with ID:', id);
          
          // Make sure the panel is expanded to a reasonable width first
          const currentWidth = alertState.previewWidth;
          if (currentWidth < 10) {
            dispatch(setPreviewWidth(30)); // 30% of container width
          }
          
          // Small delay to ensure width is set before opening the panel
          setTimeout(() => {
            dispatch(setSideModal(true));
          }, 10);
          
          console.log('Redux actions dispatched');
        } catch (error) {
          console.error("Error in onAlertClick:", error);
        }
      }
    };
  
    /**
     * Handle actions from the action popup
     */
    const onActionClick = (rowId: string, actionType: string) => {
      console.log(`Action clicked: ${actionType} for row ${rowId}`);
      // The action handling is implemented in the ActionPopup component
    };

    useEffect(() => {
      return () => {
        // This will clear the selected row on unmount/navigation
        dispatch(setSelectedRow(""));
      };
    }, [dispatch]);
  
    useEffect(() => {
      // If not currently fetching and has already instanciated the first call then fetch on the interval
      const fetchData = async () => {
        const token = await checkAuthToken();
        // Get new data
        await dispatch(setWeeklyAlerts(token));
      };
  
      fetchData();
      
      // Set the interval, every X seconds 'fetchData()' will be called
      const intervalId = setInterval(fetchData, 30 * 1000);
      
      // Once the component closes, close this interval: only fetch when in focus
      return () => {
          clearInterval(intervalId);
      };
    }, [page, pageSize, sortData, filterData]);

    const handleFilterCreated = () => {
      // Show your toast here (e.g., using notistack, MUI Snackbar, or your own)
      showToast("Custom Filter Successfully Created", "success");
      console.log("Custom filter created successfully");
    };
  return (
    <div className="flex h-full overflow-hidden" style={{ width: '100%', height: '100%' }}>
      <div className={`${alertState.previewWidth === 100 ? "" : "ps-3 pb-3 pe-3"} flex-grow flex-shrink-1 overflow-scroll`} 
      style={{ 
        flexGrow: 0, 
        flexShrink: 1,
        width: `${alertState.previewWidth ? (100 - alertState.previewWidth) : 100}%`,
        transition: 'width 0.2s ease-in-out'
      }}>
        <CategoryAlertsToolbar
          rowData={alertState.pageEvents}
          headCells={alertState.alertHeaders as IHeadCell[]}
          setFilterData={handleFilterData}
          setSearchValue={handleSearchValue}
          isFilterModalOpen={isFilterModalOpen}
          setPage={setPage}
          setIsFilterModalOpen={setIsFilterModalOpen}
          handleExportButton={handleExportButton}
          filterData={filterData}
          deleteFilter={deleteFilter}
          clearSearch={clearSearch}
          framework={framework}
          descriptions={descriptions}
          selectedDescription={selectedDescription}
          selectedSectionTitle={selectedSectionTitle}
          onFilterCreated={handleFilterCreated}
        />

          <div className="mt-3">
            <GridItem title={""}>
              <Datagrid
                rowData={alertState.pageEvents}
                headCells={alertState.alertHeaders as IHeadCell[]}
                visibleKeys={alertState.alertHeaders
                  .filter((header) => header.showColumnUnExpanded)
                  .map((header) => header.id)}
                maxWidths={{
                  entity: 200,
                  classification: 200,
                }}
                showSelectionCheckbox={false}
                showPagination={true}
                pageSize={pageSize}
                totalData={alertState.totalRowCount}
                showCustomSort={true}
                isFilterModalOpen={isFilterModalOpen}
                setIsFilterModalOpen={setIsFilterModalOpen}
                defaultSorterColumn={sortData?.field}
                onRowClick={onAlertClick}
                highlightedRowId={alertState.selectedRow}
                handleAssignedTo={handleAssignedTo}
                users={users}
                setPage={setPage}
                page={page}
                isPageLoading={alertState.areUserEventsLoading}
                statusMapping={statusMapping}
                onRowsPerPageChange={handleRowsPerPageChange}
                onPageChange={onPageChange}
                onSortChange={onSortChange}
                handleSetStatus={handleSetStatus}
              />
            </GridItem>
          </div>
      </div>
        
        {/* Resizable Side Panel for the Summary Section*/}
        <AlertSidePanel />
    </div>
  );
}
